import React from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { PhoneInput } from 'react-international-phone';

import { DateInput } from '../../../common/DateInput';
import { Input } from '../../../common/Input';
import { Select } from '../../../common/Select';
import { Field } from '../components/Field';
import { SectionHeading } from '../components/SectionHeading';
import { YesNoRadio } from '../components/YesNoRadio';
import {
  BLOOD_GROUP_OPTIONS,
  MEDICAL_COVERAGE_OPTIONS,
  RHESUS_OPTIONS,
  WOMAN_ORIGIN_OPTIONS,
  selectCls,
} from '../constants';
import type {
  BloodGroupCode,
  CoreData,
  CoverageMode,
  EducationLevel,
  MaritalStatus,
  MedicalCoverageCode,
  RhesusFactorCode,
  WomanOriginCode,
} from '../types';

interface CoreTabProps {
  core: CoreData;
  errors: Record<string, string>;
  onUpdate: <K extends keyof CoreData>(key: K, value: CoreData[K]) => void;
  onAgeValidationChange: (error: string) => void;
}

export const CoreTab: React.FC<CoreTabProps> = ({
  core,
  errors,
  onUpdate,
  onAgeValidationChange,
}) => {
  const { t } = useTranslation('Patient');
  const [ageInput, setAgeInput] = React.useState(core.age_years?.toString() ?? '');
  const ageValidationMessage = t('dossier_age_range_error', {
    min: 0,
    max: 120,
    defaultValue: 'Age must be a whole number between 0 and 120.',
  });

  React.useEffect(() => {
    setAgeInput(core.age_years?.toString() ?? '');
  }, [core.age_years]);

  const handleBirthDateChange = (value: string) => {
    onAgeValidationChange('');
    onUpdate('birth_date', value);
    if (!value) {
      if (core.age_years !== null) onUpdate('age_years', null);
      return;
    }
    const parsed = dayjs(value);
    if (!parsed.isValid()) return;
    const derivedAge = dayjs().diff(parsed, 'year');
    if (derivedAge >= 0 && derivedAge !== core.age_years) {
      onUpdate('age_years', derivedAge);
    }
  };

  const handleAgeChange = (raw: string) => {
    setAgeInput(raw);

    if (raw === '') {
      onAgeValidationChange('');
      onUpdate('age_years', null);
      return;
    }

    if (!/^\d+$/.test(raw)) {
      onAgeValidationChange(ageValidationMessage);
      if (core.age_years !== null) onUpdate('age_years', null);
      return;
    }

    const parsedAge = Number(raw);
    if (!Number.isFinite(parsedAge) || parsedAge < 0 || parsedAge > 120) {
      onAgeValidationChange(ageValidationMessage);
      if (core.age_years !== null) onUpdate('age_years', null);
      return;
    }

    onAgeValidationChange('');
    onUpdate('age_years', parsedAge);
    const derivedBirthDate = dayjs()
      .subtract(parsedAge, 'year')
      .format('YYYY-MM-DD');
    if (derivedBirthDate !== core.birth_date) {
      onUpdate('birth_date', derivedBirthDate);
    }
  };

  return (
    <div className="space-y-8">
      {/* ---------- Identity ---------- */}
      <section>
        <SectionHeading
          icon="👤"
          title={t('dossier_patient_info_title')}
          description={t('dossier_patient_info_helper')}
        />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field
            id="patient_last_name"
            label={t('lastName')}
            required
            error={errors.patient_last_name}
          >
            <Input
              id="patient_last_name"
              name="patient_last_name"
              placeholder={t('dossier_last_name_placeholder')}
              value={core.patient_last_name}
              onChange={(e) => onUpdate('patient_last_name', e.target.value)}
            />
          </Field>
          <Field
            id="patient_name"
            label={t('firstName')}
            required
            error={errors.patient_name}
          >
            <Input
              id="patient_name"
              name="patient_name"
              placeholder={t('dossier_first_name_placeholder')}
              value={core.patient_name}
              onChange={(e) => onUpdate('patient_name', e.target.value)}
            />
          </Field>
          <Field
            id="birth_date"
            label={t('dateOfBirth')}
            error={errors.birth_date}
            hint={
              errors.age_years ? undefined : t('dossier_birth_date_age_helper')
            }
          >
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <DateInput
                  id="birth_date"
                  name="birth_date"
                  value={core.birth_date}
                  onChange={(e) => handleBirthDateChange(e.target.value)}
                  max={dayjs().format('YYYY-MM-DD')}
                />
                <Input
                  id="age_years"
                  name="age_years"
                  type="number"
                  min={0}
                  max={120}
                  placeholder={t('dossier_age_placeholder')}
                  aria-label={t('dossier_age_label')}
                  aria-invalid={Boolean(errors.age_years)}
                  value={ageInput}
                  onChange={(e) => handleAgeChange(e.target.value)}
                  className="w-20"
                />
                <span className="text-xs text-title-page-color-300 dark:text-gray-400">
                  {t('dossier_age_suffix')}
                </span>
              </div>
              {errors.age_years && (
                <p className="text-xs font-medium text-red-500">
                  {errors.age_years}
                </p>
              )}
            </div>
          </Field>
          <Field id="cin" label={t('cin')} error={errors.cin}>
            <Input
              id="cin"
              name="cin"
              placeholder={t('dossier_cin_placeholder')}
              value={core.cin}
              onChange={(e) => onUpdate('cin', e.target.value)}
            />
          </Field>
          <div className="md:col-span-2">
            <Field id="address" label={t('address')} error={errors.address}>
              <Input
                id="address"
                name="address"
                placeholder={t('dossier_address_placeholder')}
                value={core.address}
                onChange={(e) => onUpdate('address', e.target.value)}
              />
            </Field>
          </div>
        </div>
      </section>

      {/* ---------- Socio-professional ---------- */}
      <section>
        <SectionHeading
          icon="💼"
          title={t('dossier_socio_section_title')}
          description={t('dossier_socio_section_helper')}
        />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field
            id="education_level"
            label={t('educationLevel')}
            error={errors.education_level}
          >
            <Select
              id="education_level"
              className={selectCls}
              value={core.education_level}
              placeholder={t('selectEducationLevel')}
              onChange={(e) =>
                onUpdate(
                  'education_level',
                  e.target.value as EducationLevel | ''
                )
              }
              options={[
                { value: 'without', label: t('educationWithout') },
                { value: 'primary', label: t('educationPrimary') },
                { value: 'secondary', label: t('educationSecondary') },
                { value: 'university', label: t('educationUniversity') },
              ]}
            />
          </Field>
          <Field
            id="occupation"
            label={t('occupation')}
            error={errors.occupation}
          >
            <Input
              id="occupation"
              name="occupation"
              placeholder={t('occupationPlaceholder')}
              value={core.occupation}
              onChange={(e) => onUpdate('occupation', e.target.value)}
            />
          </Field>
          <Field
            id="medical_coverage_label"
            label={t('dossier_medical_coverage')}
            error={errors.medical_coverage_label}
          >
            <Select
              id="medical_coverage_label"
              className={selectCls}
              value={
                MEDICAL_COVERAGE_OPTIONS.some(
                  (o) => o.value === core.medical_coverage_label
                )
                  ? core.medical_coverage_label
                  : ''
              }
              placeholder={t('dossier_medical_coverage_select')}
              onChange={(e) =>
                onUpdate(
                  'medical_coverage_label',
                  e.target.value as MedicalCoverageCode | ''
                )
              }
              options={MEDICAL_COVERAGE_OPTIONS.map((o) => ({
                value: o.value,
                label: t(o.labelKey),
              }))}
            />
          </Field>
          <Field
            id="coverage_mode"
            label={t('dossier_coverage_mode')}
            error={errors.coverage_mode}
          >
            <Select
              id="coverage_mode"
              className={selectCls}
              value={core.coverage_mode}
              placeholder={t('dossier_coverage_mode_select')}
              onChange={(e) =>
                onUpdate('coverage_mode', e.target.value as CoverageMode | '')
              }
              options={[
                { value: 'FIXE', label: t('dossier_coverage_fixe') },
                { value: 'MOBILE', label: t('dossier_coverage_mobile') },
              ]}
            />
          </Field>
          <Field
            id="woman_origin"
            label={t('dossier_origin')}
            error={errors.woman_origin}
          >
            <Select
              id="woman_origin"
              className={selectCls}
              value={
                WOMAN_ORIGIN_OPTIONS.some((o) => o.value === core.woman_origin)
                  ? core.woman_origin
                  : ''
              }
              placeholder={t('dossier_origin_select')}
              onChange={(e) =>
                onUpdate('woman_origin', e.target.value as WomanOriginCode | '')
              }
              options={WOMAN_ORIGIN_OPTIONS.map((o) => ({
                value: o.value,
                label: t(o.labelKey),
              }))}
            />
          </Field>
        </div>
      </section>

      {/* ---------- Marital & family ---------- */}
      <section>
        <SectionHeading
          icon="💍"
          title={t('dossier_marital_section_title')}
          description={t('dossier_marital_section_helper')}
        />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field
            id="marital_status"
            label={t('dossier_marital')}
            error={errors.marital_status}
          >
            <Select
              id="marital_status"
              className={selectCls}
              value={core.marital_status}
              placeholder={t('dossier_marital_select')}
              onChange={(e) =>
                onUpdate('marital_status', e.target.value as MaritalStatus | '')
              }
              options={[
                { value: 'SINGLE', label: t('dossier_marital_single') },
                { value: 'MARRIED', label: t('dossier_marital_married') },
                { value: 'WIDOWED', label: t('dossier_marital_widowed') },
                { value: 'DIVORCED', label: t('dossier_marital_divorced') },
              ]}
            />
          </Field>
          <Field
            id="consanguinity"
            label={t('consanguinity')}
            error={errors.consanguinity}
          >
            <YesNoRadio
              name="consanguinity"
              value={core.consanguinity}
              onChange={(v) => onUpdate('consanguinity', v)}
            />
          </Field>
          <Field
            id="spouse_name"
            label={t('dossier_spouse_name')}
            error={errors.spouse_name}
          >
            <Input
              id="spouse_name"
              name="spouse_name"
              placeholder={t('dossier_spouse_name_placeholder')}
              value={core.spouse_name}
              onChange={(e) => onUpdate('spouse_name', e.target.value)}
            />
          </Field>
          <Field
            id="spouse_occupation"
            label={t('dossier_spouse_occupation')}
            error={errors.spouse_occupation}
          >
            <Input
              id="spouse_occupation"
              name="spouse_occupation"
              placeholder={t('dossier_spouse_occupation_placeholder')}
              value={core.spouse_occupation}
              onChange={(e) => onUpdate('spouse_occupation', e.target.value)}
            />
          </Field>
        </div>
      </section>

      {/* ---------- Habits (dietary / toxicological) ---------- */}
      <section>
        <SectionHeading
          icon="🍃"
          title={t('dossier_habits_title')}
          description={t('dossier_habits_helper')}
        />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Field
            id="habit_caffeine"
            label={t('dossier_habit_caffeine')}
            error={errors.habit_caffeine}
          >
            <YesNoRadio
              name="habit_caffeine"
              value={core.habit_caffeine}
              onChange={(v) => onUpdate('habit_caffeine', v)}
            />
          </Field>
          <Field
            id="habit_alcohol"
            label={t('dossier_habit_alcohol')}
            error={errors.habit_alcohol}
          >
            <YesNoRadio
              name="habit_alcohol"
              value={core.habit_alcohol}
              onChange={(v) => onUpdate('habit_alcohol', v)}
            />
          </Field>
          <Field
            id="habit_tobacco"
            label={t('dossier_habit_tobacco')}
            error={errors.habit_tobacco}
          >
            <YesNoRadio
              name="habit_tobacco"
              value={core.habit_tobacco}
              onChange={(v) => onUpdate('habit_tobacco', v)}
            />
          </Field>
          <Field
            id="habit_medications"
            label={t('dossier_habit_medications')}
            error={errors.habit_medications}
          >
            <YesNoRadio
              name="habit_medications"
              value={core.habit_medications}
              onChange={(v) => onUpdate('habit_medications', v)}
            />
          </Field>
          <Field
            id="habit_geophagy"
            label={t('dossier_habit_geophagy')}
            error={errors.habit_geophagy}
          >
            <YesNoRadio
              name="habit_geophagy"
              value={core.habit_geophagy}
              onChange={(v) => onUpdate('habit_geophagy', v)}
            />
          </Field>
          <div className="md:col-span-2 lg:col-span-3">
            <Field
              id="habit_other_notes"
              label={t('dossier_habit_other')}
              error={errors.habit_other_notes}
            >
              <Input
                id="habit_other_notes"
                name="habit_other_notes"
                placeholder={t('dossier_habit_other_placeholder')}
                value={core.habit_other_notes}
                onChange={(e) => onUpdate('habit_other_notes', e.target.value)}
              />
            </Field>
          </div>
        </div>
      </section>

      {/* ---------- Blood group ---------- */}
      <section>
        <SectionHeading
          icon="🩸"
          title={t('dossier_blood_group_title')}
          description={t('dossier_blood_group_helper')}
        />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Field
            id="blood_group_abo"
            label={`${t('dossier_blood_group_patient')} — ${t('dossier_blood_group_abo')}`}
            error={errors.blood_group_abo}
          >
            <Select
              id="blood_group_abo"
              className={selectCls}
              value={core.blood_group_abo}
              placeholder={t('dossier_blood_group_abo_select')}
              onChange={(e) =>
                onUpdate(
                  'blood_group_abo',
                  e.target.value as BloodGroupCode | ''
                )
              }
              options={BLOOD_GROUP_OPTIONS.map((o) => ({
                value: o.value,
                label: t(o.labelKey),
              }))}
            />
          </Field>
          <Field
            id="blood_group_rhesus"
            label={`${t('dossier_blood_group_patient')} — ${t('dossier_blood_group_rhesus')}`}
            error={errors.blood_group_rhesus}
          >
            <Select
              id="blood_group_rhesus"
              className={selectCls}
              value={core.blood_group_rhesus}
              placeholder={t('dossier_blood_group_rhesus_select')}
              onChange={(e) =>
                onUpdate(
                  'blood_group_rhesus',
                  e.target.value as RhesusFactorCode | ''
                )
              }
              options={RHESUS_OPTIONS.map((o) => ({
                value: o.value,
                label: t(o.labelKey),
              }))}
            />
          </Field>
          <Field
            id="husband_blood_group_abo"
            label={`${t('dossier_blood_group_husband')} — ${t('dossier_blood_group_abo')}`}
            error={errors.husband_blood_group_abo}
          >
            <Select
              id="husband_blood_group_abo"
              className={selectCls}
              value={core.husband_blood_group_abo}
              placeholder={t('dossier_blood_group_abo_select')}
              onChange={(e) =>
                onUpdate(
                  'husband_blood_group_abo',
                  e.target.value as BloodGroupCode | ''
                )
              }
              options={BLOOD_GROUP_OPTIONS.map((o) => ({
                value: o.value,
                label: t(o.labelKey),
              }))}
            />
          </Field>
          <Field
            id="husband_blood_group_rhesus"
            label={`${t('dossier_blood_group_husband')} — ${t('dossier_blood_group_rhesus')}`}
            error={errors.husband_blood_group_rhesus}
          >
            <Select
              id="husband_blood_group_rhesus"
              className={selectCls}
              value={core.husband_blood_group_rhesus}
              placeholder={t('dossier_blood_group_rhesus_select')}
              onChange={(e) =>
                onUpdate(
                  'husband_blood_group_rhesus',
                  e.target.value as RhesusFactorCode | ''
                )
              }
              options={RHESUS_OPTIONS.map((o) => ({
                value: o.value,
                label: t(o.labelKey),
              }))}
            />
          </Field>
        </div>
      </section>

      {/* ---------- Weight & height ---------- */}
      <section>
        <SectionHeading
          icon="📏"
          title={t('dossier_anthropometry_title')}
          description={t('dossier_anthropometry_helper')}
        />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field
            id="weight_kg"
            label={t('dossier_weight_kg')}
            error={errors.weight_kg}
          >
            <Input
              id="weight_kg"
              name="weight_kg"
              type="number"
              min={0}
              max={500}
              step="0.1"
              placeholder={t('dossier_weight_kg_placeholder')}
              value={core.weight_kg ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') {
                  onUpdate('weight_kg', null);
                  return;
                }
                const n = Number(raw);
                if (!Number.isFinite(n)) return;
                onUpdate('weight_kg', n);
              }}
            />
          </Field>
          <Field
            id="height_cm"
            label={t('dossier_height_cm')}
            error={errors.height_cm}
          >
            <Input
              id="height_cm"
              name="height_cm"
              type="number"
              min={0}
              max={300}
              step="0.1"
              placeholder={t('dossier_height_cm_placeholder')}
              value={core.height_cm ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') {
                  onUpdate('height_cm', null);
                  return;
                }
                const n = Number(raw);
                if (!Number.isFinite(n)) return;
                onUpdate('height_cm', n);
              }}
            />
          </Field>
        </div>
      </section>

      {/* ---------- Contact ---------- */}
      <section>
        <SectionHeading
          icon="📞"
          title={t('dossier_contact_section_title')}
          description={t('dossier_contact_section_helper')}
        />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field
            id="phone_number"
            label={t('dossier_phone')}
            error={errors.phone_number}
          >
            <PhoneInput
              defaultCountry="ma"
              countrySelectorStyleProps={{
                buttonStyle: {
                  backgroundColor: 'transparent',
                  borderColor: 'rgb(203 213 225)',
                  color: 'inherit',
                },
              }}
              value={core.phone_number}
              onChange={(v) => onUpdate('phone_number', v)}
              inputClassName="w-full !bg-transparent dark:!bg-gray-800 !text-title-page-color-300"
            />
          </Field>
          <Field id="email" label={t('emailAddress')} error={errors.email}>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder={t('dossier_email_placeholder')}
              value={core.email}
              onChange={(e) => onUpdate('email', e.target.value)}
            />
          </Field>
        </div>

        {/* Built by hand rather than with the component library: this app enables
            Tailwind preflight with no antd reset, and antd styles its controls
            through zero-specificity `:where()` selectors that preflight's `*`
            rules beat, so the control renders as a blank white square. */}
        <label className="mt-4 flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            checked={!!core.reminders_opt_out}
            onChange={(e) => onUpdate('reminders_opt_out', e.target.checked)}
            className="peer sr-only"
          />
          <span
            aria-hidden="true"
            className={`mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded border transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-blue-300 ${
              core.reminders_opt_out
                ? 'border-blue-600 bg-blue-600 text-white'
                : 'border-slate-400 bg-white dark:border-gray-500 dark:bg-gray-800'
            }`}
          >
            {core.reminders_opt_out && (
              <svg
                viewBox="0 0 16 16"
                className="h-3 w-3"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 8.5l3.2 3.2L13 5" />
              </svg>
            )}
          </span>
          <span className="flex flex-col gap-1">
            <span className="text-sm font-medium text-title-page-color-300">
              {t('dossier_reminders_opt_out')}
            </span>
            <span className="text-xs text-slate-500 dark:text-gray-400">
              {t('dossier_reminders_opt_out_helper')}
            </span>
          </span>
        </label>
      </section>
    </div>
  );
};
